import type { WorkspaceContext } from '@/lib/workspace/context'
import type { TurnOutcome } from '@/lib/turnOutcome'
import { useState, useCallback, useEffect, useRef } from 'react'
import { supabase, getAuthUser } from '@/lib/supabase'
import type { AgentApiMessage, AgentProposalItem, AssistantTaskContext, AgentSourceNote } from '@/lib/agentStream'
import { runAgentTurn, type AgentTurnResult } from '@/lib/agentTurn'
import type { ChangeCardNote, ChangeSet } from '@/lib/canvas/changeCard'
import type { ChatMessage, ChatSession } from '@/types/chat'
import type { ChatAttachment } from '@/components/chat/ChatAttachment'
import { useFamilyMembers } from '@/hooks/useFamilyMembers'
import { afterPageData } from '@/lib/loadPriority'

const SESSIONS_LIMIT = 20

export interface UseSymphonyAssistantOptions {
  surface?: 'onboarding' | 'onboarding_activity'
  resolveAction?: (text: string) => Promise<AgentTurnResult | null>
  onPlanSaved?: (saved: { id: string; level: number; date: string }) => void
  onWorkspace?: (page:string,date?:string)=>void
  workspaceContext?: WorkspaceContext
  /** Called after a turn in which the agent wrote data, so the caller can
   *  refetch (the task list is not realtime for external writes). */
  onMutate?: () => void
  /** Scope the conversation to one task/routine (sent to the edge fn every turn). */
  taskContext?: AssistantTaskContext
  /** Persist conversations to chat_sessions under this entity_type (e.g.
   *  'symphony_rail', 'task'). Omit only for truly ephemeral chats. */
  persistKey?: string
  /** Link persisted conversations to a specific entity (task/routine id) so
   *  they can be surfaced on that entity's panel later. */
  persistEntityId?: string
  /** Follows each turn for the canvas: when it starts, each tool, reported
   *  write outcomes, suggestions, and how it ended. */
  turnObserver?: AssistantTurnObserver
}

export interface AssistantTurnObserver {
  start?: (turn: { text: string; turnId: string; retry: boolean }) => void
  tool?: (name: string) => void
  toolResult?: (result: { name: string; ok: boolean; ids?: string[]; error?: string }) => void
  proposal?: (items: AgentProposalItem[]) => void
  changeSet?: (set: ChangeSet) => void
  point?: (p: { dates: string[]; taskIds: string[] }) => void
  end?: (turn: { turnId: string; didWrite: boolean; error: string | null; errorCode?: string; text: string; requestId?: string }) => void
  /** The change card's state to tell this turn (read once per turn). */
  changeCard?: () => ChangeCardNote | undefined
}

type StoredMessage = { role: 'user' | 'assistant'; content: string; timestamp?: string; sources?: AgentSourceNote[] }

function serializeMessages(msgs: ChatMessage[]): StoredMessage[] {
  return msgs
    .filter((m) => m.content.trim().length > 0)
    .map((m) => ({
      role: m.role,
      content: m.content,
      timestamp: m.timestamp.toISOString(),
      ...(m.sources && m.sources.length > 0 ? { sources: m.sources } : {}),
    }))
}

function hydrateMessages(raw: unknown, sessionId: string): ChatMessage[] {
  if (!Array.isArray(raw)) return []
  return (raw as StoredMessage[]).map((m, i) => ({
    id: `${sessionId}-${i}`,
    role: m.role,
    content: m.content ?? '',
    timestamp: m.timestamp ? new Date(m.timestamp) : new Date(),
    ...(Array.isArray(m.sources) && m.sources.length > 0 ? { sources: m.sources } : {}),
  }))
}

/**
 * Right-rail assistant scoped to Symphony. Talks to the `symphony-agent`
 * edge function, which runs an Anthropic tool-use loop over the user's own
 * Symphony data (RLS-scoped). Conversation is held in React state and sent
 * with each turn; with `persistKey` set, each turn is also saved to
 * chat_sessions so conversations survive reloads (history dropdown).
 */
export function useSymphonyAssistant(options?: UseSymphonyAssistantOptions) {
  const { surface, resolveAction, onPlanSaved, onWorkspace, onMutate, taskContext, workspaceContext, persistKey, persistEntityId, turnObserver } = options ?? {}
  // The last message sent, so a failed turn can be retried with the same turn
  // id (the server can then recognise writes it already made).
  const lastTurn = useRef<{ text: string; turnId: string; changeCard?: ChangeCardNote } | null>(null)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [loading, setLoading] = useState(false)
  const sending = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [toolActivity, setToolActivity] = useState<string[]>([])
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(!!persistKey)
  const [sessionsError,setSessionsError] = useState<string|null>(null)
  const [sessionsAttempt,setSessionsAttempt] = useState(0)
  const reloadSessions=useCallback(()=>setSessionsAttempt(n=>n+1),[])
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  // The persisted row id survives across turns without waiting for state.
  const sessionIdRef = useRef<string | null>(null)
  const { getCurrentUserMember } = useFamilyMembers()

  // Recent persisted conversations for the history dropdown.
  useEffect(() => {
    if (!persistKey) return
    let cancelled = false
    ;(async () => {
      setSessionsLoading(true)
      setSessionsError(null)
      try {
        // Earlier conversations, messages and all, are not first paint: the
        // page's own data goes first (lib/loadPriority).
        await afterPageData()
        if (cancelled) return
        // mode='chat' only: the item's Discuss thread is a chat_sessions row on
        // the same entity_id, and it belongs to the drawer's main pane, not to
        // the "older conversations" dropdown.
        let query = supabase
          .from('chat_sessions')
          .select('id, title, entity_type, entity_id, mode, messages, created_at, updated_at')
          .eq('entity_type', persistKey)
          .eq('mode', 'chat')
        if (persistEntityId) query = query.eq('entity_id', persistEntityId)
        const { data,error:historyError } = await query
          .order('updated_at', { ascending: false })
          .limit(SESSIONS_LIMIT)
        if(historyError)throw historyError
        if (cancelled || !data) return
        setSessions(data.map((row) => ({
          id: row.id,
          title: row.title,
          entityType: row.entity_type,
          entityId: row.entity_id,
          mode: (row.mode ?? 'chat') as ChatSession['mode'],
          messages: hydrateMessages(row.messages, row.id),
          createdAt: new Date(row.created_at),
          updatedAt: new Date(row.updated_at),
        })))
      } catch {
        if(!cancelled)setSessionsError('Your earlier conversation could not be loaded. Your saved activities are unchanged.')
      } finally {
        if (!cancelled) setSessionsLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [persistKey, persistEntityId, sessionsAttempt])

  /** Insert or update the chat_sessions row for the current conversation. */
  const persistTurn = useCallback(async (finalMessages: ChatMessage[]) => {
    if (!persistKey) return
    try {
      const stored = serializeMessages(finalMessages)
      if (stored.length === 0) return
      if (sessionIdRef.current) {
        const { error: saveError } = await supabase
          .from('chat_sessions')
          .update({ messages: stored, updated_at: new Date().toISOString() })
          .eq('id', sessionIdRef.current)
        if (saveError) throw saveError
        setSessions((prev) => prev.map((s) =>
          s.id === sessionIdRef.current
            ? { ...s, messages: finalMessages, updatedAt: new Date() }
            : s))
      } else {
        const { data: { user } } = await getAuthUser()
        if (!user) return
        const title = stored.find((m) => m.role === 'user')?.content.slice(0, 80) ?? 'Chat'
        const { data, error: saveError } = await supabase
          .from('chat_sessions')
          .insert({ user_id: user.id, title, entity_type: persistKey, entity_id: persistEntityId ?? null, mode: 'chat', messages: stored })
          .select()
          .single()
        if (saveError || !data?.id) throw saveError ?? new Error('Conversation not saved')
        if (data?.id) {
          sessionIdRef.current = data.id
          setActiveSessionId(data.id)
          setSessions((prev) => [{
            id: data.id,
            title,
            entityType: persistKey,
            entityId: persistEntityId ?? null,
            mode: 'chat',
            messages: finalMessages,
            createdAt: new Date(),
            updatedAt: new Date(),
          }, ...prev])
        }
      }
    } catch {
      setError('Your plan changes are separate, but this conversation could not be saved. Keep this page open; your next message will retry saving the conversation.')
    }
  }, [persistKey, persistEntityId])

  /** One turn, with the server's verdict on what it did (turnOutcome.ts).
   *  Voice reads the outcome; the typed bar reads the text. */
  const sendTurn = useCallback(async (text: string, attachment?: ChatAttachment, opts?: { turnId?: string; retry?: boolean }): Promise<{ text: string; outcome: TurnOutcome; error: string | null } | undefined> => {
    if ((!text.trim() && !attachment) || sending.current) return
    sending.current = true
    setLoading(true)
    // Resolve approval/Undo before starting a planner turn: turnStarted clears
    // the prior Undo receipt. These actions own their existing canvas receipt.
    const localTurn = attachment ? null : await resolveAction?.(text)
    const turnId = opts?.turnId ?? crypto.randomUUID()
    const retry = !!opts?.retry
    // A retry tells the same card again: it was taken for the first try.
    const changeCard = localTurn ? undefined : retry && lastTurn.current?.turnId === turnId ? lastTurn.current.changeCard : turnObserver?.changeCard?.()
    lastTurn.current = { text, turnId, changeCard }
    if (!localTurn) turnObserver?.start?.({ text: text.trim(), turnId, retry })

    // Build the content for this turn: blocks array if there's an attachment, plain string otherwise.
    const content: AgentApiMessage['content'] = attachment
      ? [
          { type: 'text' as const, text: text.trim() || 'Set this up.' },
          attachment.fileType === 'application/pdf'
            ? { type: 'document' as const, source: { type: 'url' as const, url: attachment.url } }
            : { type: 'image' as const, source: { type: 'url' as const, url: attachment.url } },
        ]
      : text.trim()

    // Build the API history from prior turns + this one (before the placeholder).
    // Prior turns are displayed ChatMessages whose .content is always a string — safe to filter/map.
    const apiMessages: AgentApiMessage[] = [
      ...messages
        .filter((m) => m.content.trim().length > 0)
        .map((m) => ({ role: m.role, content: m.content })),
      { role: 'user' as const, content },
    ]

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: text.trim() || (attachment ? attachment.fileName : ''),
      timestamp: new Date(),
    }
    const assistantId = crypto.randomUUID()
    setMessages((prev) => [
      ...prev,
      userMsg,
      { id: assistantId, role: 'assistant', content: '', timestamp: new Date() },
    ])
    setLoading(true)
    setError(null)
    setToolActivity([])

    // Pass attachment metadata to the edge function so the agent can call
    // symphony_attach_source to register the uploaded PDF on the project.
    const attachmentMeta = attachment
      ? {
          storagePath: attachment.storagePath,
          fileName: attachment.fileName,
          fileType: attachment.fileType,
          fileSize: attachment.fileSize,
        }
      : undefined

    let pendingCard: ChangeSet | undefined
    let pendingProposals: AgentProposalItem[] | undefined
    const turn = localTurn ?? await runAgentTurn(apiMessages, {
      surface,
      onTool: (name) => { setToolActivity((prev) => [...prev, name]); turnObserver?.tool?.(name) },
      onToolResult: (result) => turnObserver?.toolResult?.(result),
      onProposal: (items) => { pendingProposals = items },
      onChangeSet: (set) => { pendingCard = set },
      // Beta: no live page pointing while the conversation is running.
      turnId,
      onReplyFallback: (reply) => {
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId && m.content.length === 0 ? { ...m, content: reply } : m))
      },
      onSources: (sources) => {
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId ? { ...m, sources } : m))
      },
      onError: (message) => setError(message),
      onFallbackText: (text) => {
        setMessages((prev) => prev.map((m) =>
          m.id === assistantId && m.content.length === 0 ? { ...m, content: text } : m))
      },
      attachment: attachmentMeta,
      currentMemberId: getCurrentUserMember()?.id,
      taskContext,
      workspaceContext,
      changeCard,
      onWorkspace,
      onPlanSaved,
    })

    if (!turn.error && !['failed', 'unverified'].includes(turn.outcome.state)) {
      if (pendingCard) turnObserver?.changeSet?.(pendingCard)
      if (pendingProposals) turnObserver?.proposal?.(pendingProposals)
    }
    setMessages(prev => prev.map(m => m.id === assistantId ? {...m, content:turn.text, sources:turn.sources} : m))
    const assistantSources: AgentSourceNote[] | undefined = turn.sources

    if (turn.didWrite) onMutate?.()
    if (!localTurn) turnObserver?.end?.({ turnId, didWrite: turn.didWrite, error: turn.error, ...(turn.errorCode ? { errorCode: turn.errorCode } : {}), text: turn.text, ...(turn.requestId ? { requestId: turn.requestId } : {}) })
    await persistTurn([
      ...messages,
      userMsg,
      { id: assistantId, role: 'assistant', content: turn.text, sources: assistantSources, timestamp: new Date() },
    ])
    setLoading(false)
    sending.current = false
    return { text: turn.text, outcome: turn.outcome, error: turn.error }
  }, [surface, resolveAction, onPlanSaved, onWorkspace, workspaceContext, messages, onMutate, taskContext, getCurrentUserMember, persistTurn, turnObserver])

  const sendMessage = useCallback(async (text: string, attachment?: ChatAttachment, opts?: { turnId?: string; retry?: boolean }) => (await sendTurn(text, attachment, opts))?.text, [sendTurn])

  /** Send the last message again with the same turn id. */
  const retryLast = useCallback(async () => {
    const last = lastTurn.current
    if (!last || sending.current) return
    return sendMessage(last.text, undefined, { turnId: last.turnId, retry: true })
  }, [sendMessage])

  const resetSession = useCallback(() => {
    if (sending.current) return
    setMessages([])
    setError(null)
    setToolActivity([])
    sessionIdRef.current = null
    setActiveSessionId(null)
  }, [])

  /** Restore a persisted conversation into the pane. */
  const loadSession = useCallback((session: ChatSession) => {
    if (sending.current) return
    setMessages(session.messages)
    setError(null)
    setToolActivity([])
    sessionIdRef.current = session.id
    setActiveSessionId(session.id)
  }, [])

  const deleteSession = useCallback(async (id: string) => {
    if (sending.current) return
    setSessions((prev) => prev.filter((s) => s.id !== id))
    if (sessionIdRef.current === id) resetSession()
    try {
      await supabase.from('chat_sessions').delete().eq('id', id)
    } catch {
      // best-effort
    }
  }, [resetSession])

  return {
    messages, loading, error, toolActivity, sendMessage, sendTurn, retryLast, resetSession,
    sessions, sessionsLoading, sessionsError, reloadSessions, activeSessionId, loadSession, deleteSession,
  }
}
